const API_BASE_URL = "/api";

// Function to get token from cookies
function getTokenFromCookies(): string {
  if (typeof document === "undefined") {
    return ""; // Return empty string during SSR
  }

  const cookies = document.cookie.split(";");
  const tokenCookie = cookies.find((cookie) =>
    cookie.trim().startsWith("_aicodepro_jwt=")
  );

  if (tokenCookie) {
    return tokenCookie.split("=")[1]?.trim() || "";
  }

  return "";
}

// Generic request function
async function makeRequest(endpoint: string, options: RequestInit = {}) {
  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    ...options,
    credentials: "include", // 👈 ensures HttpOnly cookies (_aicodepro_jwt) go with request
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.error || `Request failed with status ${response.status}`
    );
  }

  return await response.json();
}

// Wishlist API Functions
export const wishlistApi = {
  // Add item to wishlist
  addToWishlist: async (productId: number, variantId?: number) => {
    return await makeRequest("/wishlist", {
      method: "POST",
      body: JSON.stringify({
        productId,
        ...(variantId && { variantId }),
      }),
    });
  },

  // Get all wishlist items
  getWishlist: async () => {
    return await makeRequest("/wishlist", {
      method: "GET",
    });
  },

  // Check if item is in wishlist
  checkItemInWishlist: async (productId: number, variantId?: number) => {
    return await makeRequest("/wishlist/check", {
      method: "POST",
      body: JSON.stringify({
        productId,
        ...(variantId && { variantId }),
      }),
    });
  },

  removeFromWishlist: async (itemId: number) => {
    if (!itemId) {
      console.error("[WISHLIST API] Item ID missing");
      throw new Error("Item ID is required for removal");
    }

    try {
      const res = await makeRequest(`/wishlist/${itemId}`, {
        method: "DELETE",
      });

      return res;
    } catch (error) {
      console.error("[WISHLIST API] removeFromWishlist failed", error);
      throw error;
    }
  },

  // Move item to cart
  moveToCart: async (itemId: number) => {
    if (!itemId) throw new Error("Item ID is required for move to cart");

    return await makeRequest(`/wishlist/items/${itemId}/move-to-cart`, {
      method: "POST",
    });
  },
};

// Types (optional, for better TypeScript support)
export interface WishlistItem {
  id: string;
  productId: number;
  variantId?: number;
  // Add other properties as per your API response
}

export interface AddToWishlistParams {
  productId: number;
  variantId?: number;
}

export interface MoveToCartParams {
  itemId: string;
  cartSessionId: string;
}
